import React, { useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { uploadWebsiteBlockImage } from '../../../../services/storageService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';
import LinkInput from '../common/LinkInput';

/**
 * Returns extra style controls for the right-side style panel.
 */
export const getBannerExtraStyleControls = ({ content, onChange }) => ({
  sizeSpacingExtras: content.backgroundImage ? (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        Image
      </h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Image Position</label>
        <p className="text-xs text-gray-500 mb-2">For tall images, choose which part to show</p>
        <div className="grid grid-cols-3 gap-1.5">
          {['left', 'center', 'right'].map((align) => (
            <button
              key={align}
              type="button"
              onClick={() => onChange({ imageAlignment: align })}
              className={`py-1.5 rounded text-sm font-medium transition-colors ${
                (content.imageAlignment || 'center') === align
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {align.charAt(0).toUpperCase() + align.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">
          Overlay Darkness: {Math.round((content.overlayOpacity || 0.5) * 100)}%
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={content.overlayOpacity || 0.5}
          onChange={(e) => onChange({ overlayOpacity: parseFloat(e.target.value) })}
          className="w-full accent-[#749555]"
        />
      </div>
    </div>
  ) : null,
});

/**
 * BannerEditor - Editor component for banner block
 */
const BannerEditor = ({ block, onChange, profileId, theme, hideStylePanels, pages }) => {
  const { content } = block;
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError('');

    try {
      const downloadURL = await uploadWebsiteBlockImage(file, profileId, block.id);
      handleChange('backgroundImage', downloadURL);
    } catch (error) {
      setUploadError(error.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    handleChange('backgroundImage', '');
  };

  const features = BLOCK_FEATURES['banner'];
  const extras = getBannerExtraStyleControls({ content, onChange });

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Enter heading..."
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Subheading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Subheading
        </label>
        <textarea
          value={content.subheading || ''}
          onChange={(e) => handleChange('subheading', e.target.value)}
          placeholder="Enter subheading..."
          rows={3}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none"
        />
      </div>

      {/* Background Image */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Background Image
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleImageUpload}
          className="hidden"
        />

        {content.backgroundImage ? (
          <div className="relative rounded-lg overflow-hidden">
            <img
              src={content.backgroundImage}
              alt="Banner background"
              className="w-full h-32 object-cover"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full h-32 border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-[#749555] hover:text-[#749555] transition-colors disabled:opacity-50"
          >
            {isUploading ? (
              <Loader2 size={24} className="animate-spin" />
            ) : (
              <>
                <Upload size={24} />
                <span className="text-sm mt-2">Upload Image</span>
              </>
            )}
          </button>
        )}

        {uploadError && (
          <p className="text-red-400 text-sm mt-1">{uploadError}</p>
        )}
      </div>

      {/* Button Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Button Text (optional)
        </label>
        <input
          type="text"
          value={content.buttonText || ''}
          onChange={(e) => handleChange('buttonText', e.target.value)}
          placeholder="Learn More"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Button Link */}
      {content.buttonText && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Button Link
          </label>
          <LinkInput
            value={content.buttonLink || ''}
            onChange={(val) => handleChange('buttonLink', val)}
            pages={pages}
          />
        </div>
      )}

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={features}
            extraControls={extras.sizeSpacingExtras}
          />
          <ColorsFontsPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={features}
            theme={theme || {}}
            blockType="banner"
          />
        </>
      )}
    </div>
  );
};

export default BannerEditor;
